/** P-07 — patient prescription detail + "Order this Rx" (P-09). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  getPrescription,
  listAddresses,
  type Prescription,
} from "../../api/patient";
import { placeOrder } from "../../api/orders";
import { getDoseReminders, setDoseReminders } from "../../api/notifications";
import { ApiError, describeError } from "../../api/errors";
import { useIdempotencyKey } from "../../api/useIdempotencyKey";
import { Modal } from "../../components/ui/Modal";
import { formatDose } from "../../lib/rxLines";
import { ScheduleBadge, StatusBadge } from "./Prescriptions";

export function PrescriptionDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const {
    data: rx,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["prescription", id],
    queryFn: () => getPrescription(id),
  });
  const [orderOpen, setOrderOpen] = useState(false);

  if (isLoading) return <p className="text-sm text-gray-400">Loading…</p>;
  if (isError || !rx)
    return <p className="text-sm text-red-600">Prescription not found.</p>;

  // Orderable only while active and not deactivated (ORD-11): an expired Rx showed "Active" and
  // "Order this Rx", which the API then refused (RX_EXPIRED).
  const deactivated = rx.is_active === false;
  const orderable = rx.status === "active" && !deactivated;

  return (
    <div className="max-w-2xl space-y-5">
      <Link
        to="/patient/prescriptions"
        className="text-sm text-brand hover:underline"
      >
        ← Back to prescriptions
      </Link>

      <div className="rounded-xl border bg-white p-5 shadow-xs">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <ScheduleBadge cls={rx.schedule_class} />
            {deactivated ? (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                No longer available
              </span>
            ) : (
              <StatusBadge status={rx.status} />
            )}
          </div>
          {orderable && (
            <button
              onClick={() => setOrderOpen(true)}
              className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg shadow-xs hover:opacity-90"
            >
              Order this Rx
            </button>
          )}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <Info
            label="Issued"
            value={new Date(rx.issued_at).toLocaleDateString()}
          />
          <Info
            label="Valid till"
            value={new Date(rx.expires_at).toLocaleDateString()}
          />
        </dl>
        {rx.notes && (
          <p className="mt-3 rounded-lg bg-gray-50 p-3 text-sm">{rx.notes}</p>
        )}
      </div>

      <DoctorCard rx={rx} />

      <div className="rounded-xl border bg-white shadow-xs">
        <div className="border-b px-5 py-3 text-sm font-medium text-gray-500">
          Medicines
        </div>
        <ul className="divide-y">
          {rx.lines.map((ln) => (
            <li key={ln.id} className="px-5 py-3">
              {/* A long name wraps on its own side; the quantity keeps its place and one line (RESP-12). */}
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0 font-medium wrap-anywhere">{ln.medicine_name}</span>
                <span className="shrink-0 whitespace-nowrap text-sm text-gray-500">Qty {ln.quantity}</span>
              </div>
              <div className="mt-1 text-sm text-gray-600">
                {formatDose(ln.dosage_value, ln.dosage_unit)} ·{" "}
                {ln.food_relation.replace("_", " ")} ·{" "}
                {ln.time_of_day.join(", ")}
                {ln.duration_days ? ` · ${ln.duration_days} days` : ""}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <DoseReminderCard prescriptionId={rx.id} />

      <Modal
        open={orderOpen}
        title="Place order"
        onClose={() => setOrderOpen(false)}
      >
        <PlaceOrderForm
          prescriptionId={rx.id}
          onCancel={() => setOrderOpen(false)}
          onPlaced={(orderId) => navigate(`/patient/orders/${orderId}`)}
        />
      </Modal>
    </div>
  );
}

function DoctorCard({ rx }: { rx: Prescription }) {
  // Prefer the doctor attributed on a digitised paper Rx; otherwise the author (a doctor-created
  // Rx). For OTC/self uploads with no doctor attached, there's nothing to show.
  const doctorName = rx.on_behalf_of_doctor_name ?? rx.authored_by_name;
  if (!doctorName) return null;

  return (
    <div className="rounded-xl border bg-white p-5 shadow-xs">
      <div className="text-sm font-medium text-gray-500">Prescribed by</div>
      <div className="mt-1 font-medium text-gray-900">{doctorName}</div>
      {rx.doctor_specialisation && (
        <div className="text-sm text-gray-600">{rx.doctor_specialisation}</div>
      )}
    </div>
  );
}

function DoseReminderCard({ prescriptionId }: { prescriptionId: string }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["dose-reminders", prescriptionId],
    queryFn: () => getDoseReminders(prescriptionId),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      setDoseReminders(prescriptionId, { enabled }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["dose-reminders", prescriptionId] }),
  });

  return (
    <div className="flex items-center justify-between rounded-xl border bg-white p-5 shadow-xs">
      <div>
        <div className="text-sm font-medium">Dose reminders</div>
        <div className="text-xs text-gray-500">
          {data?.enabled
            ? `On · ${data.reminder_count} reminders scheduled`
            : "Get a notification at each dose time."}
        </div>
      </div>
      <button
        type="button"
        onClick={() => toggle.mutate(!data?.enabled)}
        disabled={toggle.isPending}
        className={`flex h-6 w-11 items-center rounded-full p-0.5 transition ${data?.enabled ? "bg-brand" : "bg-gray-300"}`}
      >
        <span
          className={`h-5 w-5 rounded-full bg-white transition ${data?.enabled ? "translate-x-5" : ""}`}
        />
      </button>
    </div>
  );
}

function PlaceOrderForm({
  prescriptionId,
  onCancel,
  onPlaced,
}: {
  prescriptionId: string;
  onCancel: () => void;
  onPlaced: (orderId: string) => void;
}) {
  const { data: addresses = [] } = useQuery({
    queryKey: ["addresses"],
    queryFn: listAddresses,
  });
  const [addressId, setAddressId] = useState<string>("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const placeKey = useIdempotencyKey();

  // default to the default address
  const defaultId =
    addresses.find((a) => a.is_default)?.id ?? addresses[0]?.id ?? "";
  const selected = addressId || defaultId;

  const place = useMutation({
    mutationFn: () => {
      const body = {
        prescription_id: prescriptionId,
        delivery_address_id: selected,
        placement_note: note.trim() || undefined,
      };
      return placeOrder(body, placeKey.keyFor(body));
    },
    onSuccess: (order) => {
      placeKey.reset();
      onPlaced(order.id);
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.code === "RX_EXPIRED"
          ? "This prescription has expired."
          : e instanceof ApiError && e.code === "RX_ALREADY_CONSUMED"
            ? "This prescription has already been ordered."
            : describeError(e),
      ),
  });

  if (addresses.length === 0) {
    return (
      <div className="space-y-3 text-sm">
        <p className="text-gray-600">
          You need a delivery address before ordering.
        </p>
        <Link to="/patient/addresses" className="text-brand hover:underline">
          Add an address →
        </Link>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        place.mutate();
      }}
      className="space-y-4"
    >
      <div className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">Deliver to</span>
        <div className="grid max-h-64 gap-2 overflow-y-auto pr-1">
          {addresses.map((a) => {
            const isSelected = a.id === selected;
            return (
              <label
                key={a.id}
                className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition ${
                  isSelected
                    ? "border-brand bg-brand/5 ring-1 ring-brand"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="delivery-address"
                  value={a.id}
                  checked={isSelected}
                  onChange={(e) => setAddressId(e.target.value)}
                  className="mt-1 accent-brand"
                />
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium text-gray-900">
                    {a.label || a.name}
                    {a.is_default && (
                      <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-normal text-emerald-700">
                        Default
                      </span>
                    )}
                  </span>
                  <span className="text-gray-600">
                    {a.line1}
                    {a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state_code}{" "}
                    {a.pin_code}
                  </span>
                  <span className="text-xs text-gray-400">{a.phone}</span>
                </div>
              </label>
            );
          })}
        </div>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-gray-500">
          Note for the pharmacist (optional)
        </span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. deliver after 6pm"
          className="rounded-lg border px-3 py-1.5"
        />
      </label>
      <p className="text-xs text-gray-400">
        A pharmacist will price your order; you'll pay after that.
      </p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg"
          disabled={place.isPending}
        >
          {place.isPending ? "Placing…" : "Place order"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border px-4 py-2 text-sm"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-gray-400">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
